import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, VStack, HStack, Text, SimpleGrid, Icon, Flex,
  Input, Textarea, Select, Button, Divider, useToast,
  Tag, Spinner, Alert, AlertIcon,
} from '@chakra-ui/react';
import {
  FiUser, FiEdit2, FiCheck, FiX, FiAlertTriangle,
  FiRefreshCw, FiCalendar, FiChevronRight,
  FiUserCheck, FiBookOpen, FiHeart, FiFileText,
  FiAward, FiFolder, FiActivity, FiHome, FiUsers,
} from 'react-icons/fi';
import { updateCustomer } from 'services/woo';
import { loadFormSyncConfigs, syncFormResponseToCustomer, buildResponseDataFromQuestions } from 'services/formSync';
import { getSurveyResponse } from 'services/surveyService';
import { getStudentAppointments } from 'services/appointmentBooking';
import { codeToDisplayLabel, normalizeCourseName } from 'utils/courseUtils';
import { useUserAuth } from 'utils/site';

// ── helpers ────────────────────────────────────────────────────────────────────

const getMeta = (customer, key) =>
  customer.meta_data?.find(m => m.key === key)?.value || '';

const prettify = (key) =>
  (key || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const formatFecha = (val) => {
  if (!val) return '';
  const d = new Date(val + (val.length === 10 ? 'T12:00:00' : ''));
  if (isNaN(d)) return val;
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatFichaDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const calcEdad = (fechaNac) => {
  if (!fechaNac) return '';
  const d = new Date(fechaNac + (fechaNac.length === 10 ? 'T12:00:00' : ''));
  if (isNaN(d)) return '';
  const hoy = new Date();
  let edad = hoy.getFullYear() - d.getFullYear();
  if (hoy.getMonth() < d.getMonth() ||
    (hoy.getMonth() === d.getMonth() && hoy.getDate() < d.getDate())) edad--;
  return `${edad} años`;
};

// ── sub-components ─────────────────────────────────────────────────────────────

const SectionCard = ({ icon, iconBg = 'coral.soft', iconColor = 'coral.700', title, subtitle, onEdit, editLabel, editing, children }) => (
  <Box bg="white" borderRadius="20px" border="1px solid" borderColor="cream.line" overflow="hidden">
    <Flex align="center" gap="12px" px="20px" py="16px" borderBottom="1px solid" borderColor="cream.line">
      <Flex w="32px" h="32px" borderRadius="10px" align="center" justify="center"
        bg={iconBg} color={iconColor} flex="0 0 auto">
        <Icon as={icon} boxSize="15px" />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontWeight="700" fontSize="14px" fontFamily="jakarta" color="ink.900">
          {title}
        </Text>
        {subtitle && (
          <Text fontSize="11px" color="ink.400" mt="1px">{subtitle}</Text>
        )}
      </Box>
      {onEdit && !editing && (
        <Text as="button" fontSize="12.5px" fontWeight="700" color="coral.500" cursor="pointer"
          _hover={{ color: 'coral.700' }} onClick={onEdit} display="flex" alignItems="center" gap="4px">
          <Icon as={editLabel ? FiCalendar : FiEdit2} boxSize="12px" /> {editLabel || 'Editar'}
        </Text>
      )}
    </Flex>
    <Box px="20px" py="16px">{children}</Box>
  </Box>
);

const Field = ({ label, value, muted, children }) => (
  <Box>
    <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase"
      color="ink.400" mb="3px">{label}</Text>
    {children || (
      <Text fontSize="14px" fontWeight="600" fontFamily="jakarta"
        color={muted || !value ? 'ink.300' : 'ink.900'}>
        {value || 'No registrado'}
      </Text>
    )}
  </Box>
);

const EditInput = ({ label, value, onChange, type = 'text' }) => (
  <Box>
    <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase"
      color="ink.400" mb="4px">{label}</Text>
    {type === 'textarea' ? (
      <Textarea size="sm" borderRadius="10px" borderColor="cream.line" bg="cream.bg" rows={3}
        value={value || ''} onChange={e => onChange(e.target.value)} />
    ) : (
      <Input size="sm" borderRadius="10px" borderColor="cream.line" bg="cream.bg"
        type={type === 'phone' ? 'tel' : type === 'number' ? 'number' : type === 'date' ? 'date' : type === 'email' ? 'email' : 'text'}
        value={value || ''} onChange={e => onChange(e.target.value)} />
    )}
  </Box>
);

const SaveBar = ({ onSave, onCancel, saving }) => (
  <HStack justify="flex-end" mt="16px" pt="14px" borderTop="1px solid" borderColor="cream.line" spacing="8px">
    <Button size="sm" variant="ghost" borderRadius="9px" onClick={onCancel} isDisabled={saving}
      leftIcon={<Icon as={FiX} />}>Cancelar</Button>
    <Button size="sm" bg="coral.500" color="white" borderRadius="9px" _hover={{ bg: 'coral.600' }}
      onClick={onSave} isLoading={saving} leftIcon={<Icon as={FiCheck} />}>Guardar</Button>
  </HStack>
);

// ── Card dinámico — renderiza los campos de un config ─────────────────────────

// Mapa categoría → icono + colores pastel
const CATEGORY_CONFIG = {
  atencion:   { icon: FiUserCheck,     iconBg: 'coral.soft', iconColor: 'coral.700' },
  atención:   { icon: FiUserCheck,     iconBg: 'coral.soft', iconColor: 'coral.700' },
  matriculas: { icon: FiBookOpen,      iconBg: 'sky.bg',     iconColor: 'sky.ink'   },
  matrícula:  { icon: FiBookOpen,      iconBg: 'sky.bg',     iconColor: 'sky.ink'   },
  matricula:  { icon: FiBookOpen,      iconBg: 'sky.bg',     iconColor: 'sky.ink'   },
  salud:      { icon: FiHeart,         iconBg: 'rose.bg',    iconColor: 'rose.ink'  },
  medico:     { icon: FiActivity,      iconBg: 'rose.bg',    iconColor: 'rose.ink'  },
  médico:     { icon: FiActivity,      iconBg: 'rose.bg',    iconColor: 'rose.ink'  },
  documentos: { icon: FiFileText,      iconBg: 'mint.bg',    iconColor: 'mint.ink'  },
  documento:  { icon: FiFileText,      iconBg: 'mint.bg',    iconColor: 'mint.ink'  },
  becas:      { icon: FiAward,         iconBg: 'lav.bg',     iconColor: 'lav.ink'   },
  beca:       { icon: FiAward,         iconBg: 'lav.bg',     iconColor: 'lav.ink'   },
  casos:      { icon: FiAlertTriangle, iconBg: 'butter.bg',  iconColor: 'butter.ink'},
  caso:       { icon: FiAlertTriangle, iconBg: 'butter.bg',  iconColor: 'butter.ink'},
  familia:    { icon: FiUsers,         iconBg: 'teal.bg',    iconColor: 'teal.ink'  },
  apoderado:  { icon: FiUsers,         iconBg: 'teal.bg',    iconColor: 'teal.ink'  },
  domicilio:  { icon: FiHome,          iconBg: 'amber.bg',   iconColor: 'amber.ink' },
  direccion:  { icon: FiHome,          iconBg: 'amber.bg',   iconColor: 'amber.ink' },
  otros:      { icon: FiFolder,        iconBg: 'cream.bg2',  iconColor: 'ink.700'   },
};

// Fallback por índice cuando la categoría no coincide
const FALLBACK_COLORS = [
  { iconBg: 'sky.bg',    iconColor: 'sky.ink'    },
  { iconBg: 'mint.bg',   iconColor: 'mint.ink'   },
  { iconBg: 'lav.bg',    iconColor: 'lav.ink'    },
  { iconBg: 'rose.bg',   iconColor: 'rose.ink'   },
  { iconBg: 'butter.bg', iconColor: 'butter.ink' },
  { iconBg: 'teal.bg',   iconColor: 'teal.ink'   },
];

const getCategoryConfig = (category, colorIdx = 0) => {
  const key = (category || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  if (CATEGORY_CONFIG[key]) return CATEGORY_CONFIG[key];
  const fallback = FALLBACK_COLORS[colorIdx % FALLBACK_COLORS.length];
  return { icon: FiFolder, ...fallback };
};

const DynamicCard = ({ config, customer, onUpdate, colorIdx = 0, userSurveys }) => {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Solo mapeos que van a meta_data
  const metaMappings = (config.fieldMap || []).filter(m => m.target === 'meta');

  // Ficha más reciente de esta categoría — misma que usa el (auto-)sync
  const latestFicha = (userSurveys || []).find(s => s.survey?.category === config.category);
  const fichaSubtitle = latestFicha?.created_at ? `Ficha del ${formatFichaDate(latestFicha.created_at)}` : null;

  const startEdit = () => {
    const vals = {};
    metaMappings.forEach(m => { vals[m.key] = getMeta(customer, m.key); });
    setEditValues(vals);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const meta_data = Object.entries(editValues).map(([key, value]) => ({ key, value: value || '' }));
      // Registrar qué claves cambiaron a mano: el auto-sync preguntará antes de pisarlas
      const changed = Object.entries(editValues)
        .filter(([key, value]) => (value || '') !== getMeta(customer, key))
        .map(([key]) => `meta:${key}`);
      if (changed.length) {
        meta_data.push({ key: MANUAL_KEYS_META, value: JSON.stringify([...new Set([...getManualKeys(customer), ...changed])]) });
      }
      await updateCustomer(customer.id, { meta_data });
      await onUpdate();
      setEditing(false);
      toast({ title: 'Guardado correctamente', status: 'success', duration: 2500, isClosable: true });
    } catch {
      toast({ title: 'Error al guardar', status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const catConfig = getCategoryConfig(config.category, colorIdx);

  // Deduplicar: si la misma key aparece varias veces (aliases), mostrar solo la primera
  const seen = new Set();
  const uniqueMappings = metaMappings.filter(m => {
    if (seen.has(m.key)) return false;
    seen.add(m.key);
    return true;
  });

  if (uniqueMappings.length === 0) return null;

  return (
    <SectionCard
      icon={catConfig.icon}
      iconBg={catConfig.iconBg}
      iconColor={catConfig.iconColor}
      title={config.description || prettify(config.category) || config.slug}
      subtitle={fichaSubtitle}
      editing={editing}
      onEdit={startEdit}
    >
      {editing ? (
        <>
          <SimpleGrid columns={2} spacing="12px">
            {uniqueMappings.map(m => (
              <EditInput
                key={m.key}
                label={m.label || prettify(m.key)}
                value={editValues[m.key]}
                onChange={val => setEditValues(prev => ({ ...prev, [m.key]: val }))}
                type={m.type || 'text'}
              />
            ))}
          </SimpleGrid>
          <SaveBar onSave={saveEdit} onCancel={() => setEditing(false)} saving={saving} />
        </>
      ) : (
        <SimpleGrid columns={2} spacing="14px">
          {uniqueMappings.map(m => {
            const value = getMeta(customer, m.key);
            const label = m.label || prettify(m.key);
            const type  = m.type || 'text';
            const display = type === 'date' ? formatFecha(value) : value;
            return <Field key={m.key} label={label} value={display} muted={!value} />;
          })}
        </SimpleGrid>
      )}
    </SectionCard>
  );
};

// ── Backfill — sincroniza desde fichas existentes ──────────────────────────────
// Compartido entre el botón manual y el auto-sync al entrar al tab.

// Todos los campos mapeados (fieldMap + syncAlso) de una categoría
const fieldsForCategory = (configs, category) =>
  Object.values(configs).filter(c => c.category === category).flatMap(c => c.fieldMap || []);

// ── Control del sync ───────────────────────────────────────────────────────────
// sync_ficha_<categoria>  → "id:updated_at" de la ficha ya volcada; evita re-sincronizar
//                           (y re-preguntar) mientras no haya una ficha nueva o modificada.
// sync_manual_keys        → JSON con ["meta:clave", ...] editadas a mano con el botón Editar.
//                           Son las únicas que el auto-sync pregunta antes de pisar.
const MANUAL_KEYS_META = 'sync_manual_keys';
const fichaMarkerKey   = (category) => `sync_ficha_${category}`;
const fichaMarker      = (ficha) => `${ficha.id}:${ficha.updated_at || ficha.created_at || ''}`;

const getManualKeys = (customer) => {
  try {
    const parsed = JSON.parse(getMeta(customer, MANUAL_KEYS_META) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

// Targets (mapeo + syncAlso) de un campo mapeado
const targetsOf = (m) => [{ target: m.target, key: m.key }, ...(m.syncAlso || [])];

const currentValue = (customer, t) => {
  if (t.target === 'meta')    return getMeta(customer, t.key);
  if (t.target === 'billing') return customer.billing?.[t.key] || '';
  if (t.target === 'root')    return customer[t.key] || '';
  return '';
};

// mode='auto'  → al entrar al tab: aplica la última ficha de cada categoría (solo si es
//   nueva respecto a la ya volcada). Los campos editados a mano que difieran de la ficha
//   se consultan con confirm(); el resto se sobrescribe sin preguntar.
// mode='force' → botón manual: sobrescribe todo con la última ficha y limpia las marcas
//   de edición manual de esos campos.
const runBackfillSync = async ({ customer, userSurveys, configs, mode = 'auto' }) => {
  const categories = [...new Set(Object.values(configs).map(c => c.category))];
  let manualKeys = getManualKeys(customer);
  let synced = 0;

  for (const category of categories) {
    const fields = fieldsForCategory(configs, category);

    // Última ficha de esta categoría
    const latest = (userSurveys || []).find(s => s.survey?.category === category);
    if (!latest?.id) continue;

    const marker = fichaMarker(latest);
    // El marcador solo evita RE-PREGUNTAR por ediciones manuales de una ficha ya resuelta.
    // No decide si se sincroniza: siempre se compara el valor real del customer contra la ficha,
    // así un dato que cambió por otra vía (otro sync, edición externa) se vuelve a alinear.
    const fichaAlreadyResolved = getMeta(customer, fichaMarkerKey(category)) === marker;

    try {
      const full = await getSurveyResponse(latest.id);
      if (!full?.questions) continue;
      // Nunca escribir una ficha que no pertenezca a este alumno
      if (String(full.respondent?.username || '').toLowerCase() !== String(customer.username || '').toLowerCase()) continue;

      const responseData = buildResponseDataFromQuestions(full.questions,
        Object.fromEntries(full.questions.map(q => [q.id, q.response?.answer || '']))
      );

      // Conflictos: campos editados a mano cuyo valor actual difiere de la ficha
      const skipKeys = new Set();
      const conflictLabels = [];
      if (mode === 'auto') {
        fields.forEach(m => {
          const v = responseData[m.formField];
          if (!v) return;
          const manualHits = targetsOf(m).filter(t =>
            manualKeys.includes(`${t.target}:${t.key}`) && currentValue(customer, t) !== v);
          if (manualHits.length) {
            manualHits.forEach(t => skipKeys.add(`${t.target}:${t.key}`));
            if (!fichaAlreadyResolved) conflictLabels.push(m.label || prettify(m.key));
          }
        });

        if (conflictLabels.length) {
          const overwrite = window.confirm(
            `La ficha "${latest.survey?.title || category}" tiene datos distintos a los que editaste manualmente:\n\n` +
            `• ${[...new Set(conflictLabels)].join('\n• ')}\n\n` +
            '¿Reemplazarlos con los datos de la ficha?\n(Cancelar conserva tus ediciones)'
          );
          if (overwrite) skipKeys.clear();
        }
      }

      // ¿Algo que escribir? (valor distinto en algún target no omitido, o marcador por actualizar)
      if (mode === 'auto' && fichaAlreadyResolved) {
        const hasDiff = fields.some(m => {
          const v = responseData[m.formField];
          return v && targetsOf(m).some(t => !skipKeys.has(`${t.target}:${t.key}`) && currentValue(customer, t) !== v);
        });
        if (!hasDiff) continue;
      }

      // Claves manuales que quedan vigentes: las que se conservaron (en force se limpian todas las de la categoría)
      const categoryKeys = new Set(fields.flatMap(targetsOf).map(t => `${t.target}:${t.key}`));
      manualKeys = manualKeys.filter(k => !categoryKeys.has(k) || skipKeys.has(k));

      const result = await syncFormResponseToCustomer({
        formSlug: latest.survey?.slug || category,
        formCategory: category,
        responseData,
        customerId: customer.id,
        skipKeys,
        extraMeta: [
          { key: fichaMarkerKey(category), value: marker },
          { key: MANUAL_KEYS_META, value: JSON.stringify(manualKeys) },
        ],
      });

      if (result.synced) synced++;
    } catch { /* continuar con siguiente */ }
  }

  return { synced };
};

// Botón de re-sincronización forzada (mode 'force'): sobrescribe todo con la última
// ficha del alumno, sin preguntar por ediciones manuales (pide confirmación general).
const SHOW_BACKFILL_BUTTON = true;

const BackfillButton = ({ customer, userSurveys, configs, onUpdate, disabled }) => {
  const toast = useToast();
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    if (!window.confirm('Esto sobrescribirá los campos de Antecedentes con los datos de las últimas fichas de este alumno. ¿Continuar?')) return;
    setRunning(true);
    const { synced } = await runBackfillSync({ customer, userSurveys, configs, mode: 'force' });
    if (synced > 0) await onUpdate();
    setRunning(false);
    toast({
      title: synced > 0 ? `${synced} sección(es) sincronizada(s)` : 'Sin datos para sincronizar',
      status: synced > 0 ? 'success' : 'info',
      duration: 3000,
      isClosable: true,
    });
  }, [customer, userSurveys, configs, onUpdate, toast]);

  return (
    <Button size="sm" variant="outline" borderColor="cream.line" color="ink.700" borderRadius="10px"
      fontWeight="700" fontFamily="jakarta" leftIcon={<Icon as={FiRefreshCw} boxSize="13px" />}
      _hover={{ borderColor: 'coral.500', color: 'coral.700' }}
      onClick={run} isLoading={running} isDisabled={disabled} loadingText="Sincronizando...">
      Re-sincronizar (sobrescribir) desde fichas
    </Button>
  );
};

// ── Card estático: Datos personales (desde customer, no meta_data sync) ───────

const DatosPersonalesCard = ({ customer }) => {
  const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Sin nombre';
  const groupRaw = getMeta(customer, 'group_name') || getMeta(customer, 'grupo');
  const groupLabel = groupRaw ? codeToDisplayLabel(normalizeCourseName(groupRaw)) : null;
  const fechaNac = getMeta(customer, 'fecha_nacimiento');

  return (
    <SectionCard icon={FiUser} iconBg="coral.soft" iconColor="coral.700" title="Datos personales">
      <SimpleGrid columns={2} spacing="14px">
        <Field label="Nombre completo" value={fullName} />
        <Field label="RUT" value={customer.username} />
        {fechaNac && <Field label="Fecha de nacimiento" value={formatFecha(fechaNac)} />}
        {fechaNac && <Field label="Edad" value={calcEdad(fechaNac)} />}
      </SimpleGrid>
      {(groupLabel || getMeta(customer, 'suscription_name') || customer.date_created) && (
        <>
          <Divider my="14px" borderColor="cream.line" />
          <SimpleGrid columns={3} spacing="14px">
            <Field label="Curso">
              {groupLabel
                ? <Tag size="sm" bg="sky.bg" color="sky.ink" borderRadius="8px" fontWeight="700" fontSize="12.5px" px="9px">{groupLabel}</Tag>
                : <Text fontSize="14px" color="ink.300">Sin curso</Text>}
            </Field>
            <Field label="Fecha de ingreso"
              value={customer.date_created
                ? new Date(customer.date_created).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
                : null}
              muted={!customer.date_created} />
            <Field label="Plan" value={getMeta(customer, 'suscription_name')} muted={!getMeta(customer, 'suscription_name')} />
          </SimpleGrid>
        </>
      )}
    </SectionCard>
  );
};

// ── Card de citas — hoy + pasadas, resumen compacto ───────────────────────────

const CITA_STATUS = {
  pending:   { bg: '#FBEECB', color: '#C77A12', label: 'Pendiente' },
  confirmed: { bg: '#DCF3E7', color: '#1E9E63', label: 'Confirmada' },
  completed: { bg: '#E8E8E8', color: '#6E655C', label: 'Realizada' },
  cancelled: { bg: '#FBE0D9', color: '#D6452B', label: 'Cancelada' },
};

const isToday = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
};

const ProximaCitaCard = ({ studentRut }) => {
  const [citas, setCitas]     = useState([]);
  const [loading, setLoading] = useState(true);
  const { user: authUser }    = useUserAuth();

  useEffect(() => {
    if (!studentRut) { setLoading(false); return; }
    // dateTo = mañana para incluir hoy completo; sin dateFrom = todas las pasadas también
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateTo = tomorrow.toISOString().slice(0, 10);

    getStudentAppointments(studentRut, { dateTo })
      .then(data => {
        const items = Array.isArray(data) ? data : (data?.data || []);
        // más reciente primero
        setCitas([...items].sort((a, b) => {
          const da = new Date(`${a.appointment_date}T${a.start_time}`);
          const db = new Date(`${b.appointment_date}T${b.start_time}`);
          return db - da;
        }));
      })
      .catch(() => setCitas([]))
      .finally(() => setLoading(false));
  }, [studentRut]);

  const handleNewCita = () => {
    if (!authUser?.id || !studentRut) return;
    const url = `${window.location.origin}/#/public/reservar-hora/${authUser.id}?student_rut=${encodeURIComponent(studentRut)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <SectionCard
      icon={FiCalendar}
      iconBg="mint.bg"
      iconColor="mint.ink"
      title="Citas"
      onEdit={handleNewCita}
      editLabel="+ Nueva cita"
    >
      {loading ? (
        <Flex align="center" gap="8px" py="4px">
          <Spinner size="sm" color="coral.500" />
          <Text fontSize="13px" color="ink.400">Cargando...</Text>
        </Flex>
      ) : citas.length === 0 ? (
        <HStack justify="space-between">
          <Text fontSize="13.5px" color="ink.400" fontWeight="500">Sin citas registradas</Text>
          <Text as="a" href="#/admin/agenda" fontSize="12.5px" fontWeight="700"
            color="coral.500" _hover={{ color: 'coral.700' }}
            display="flex" alignItems="center" gap="4px">
            Ver agenda <Icon as={FiChevronRight} boxSize="12px" />
          </Text>
        </HStack>
      ) : (
        <Box>
          <VStack spacing="0" align="stretch" divider={<Box borderTop="1px solid" borderColor="cream.line" />}>
            {citas.slice(0, 6).map(c => {
              const dateStr = `${c.appointment_date}T${c.start_time || '00:00:00'}`;
              const d       = new Date(dateStr);
              const chip    = CITA_STATUS[c.status] || CITA_STATUS.pending;
              const hoy     = isToday(c.appointment_date);
              const hora    = c.start_time ? c.start_time.slice(0, 5) : '';
              const fecha   = hoy
                ? `Hoy ${hora}`
                : d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) + (hora ? ` ${hora}` : '');
              const titulo  = c.reason || c.notes || c.teacher_name || 'Cita';

              return (
                <Flex key={c.id} py="9px" gap="10px" align="flex-start">
                  <Box flex="0 0 auto">
                    <Box w="7px" h="7px" borderRadius="50%"
                      bg={hoy ? 'coral.500' : 'ink.300'} mt="5px" />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="12.5px" fontWeight="700"
                      color={hoy ? 'coral.600' : 'ink.700'} noOfLines={1}>
                      {fecha}
                    </Text>
                    <Text fontSize="12px" color="ink.400" noOfLines={1}>{titulo}</Text>
                  </Box>
                  <Box as="span" px="8px" py="2px" borderRadius="999px" flexShrink={0}
                    bg={chip.bg} color={chip.color} fontSize="11px" fontWeight="700">
                    {chip.label}
                  </Box>
                </Flex>
              );
            })}
          </VStack>
          <Flex justify="flex-end" mt="10px" pt="8px" borderTop="1px solid" borderColor="cream.line">
            <Text as="a" href="#/admin/agenda" fontSize="12px" fontWeight="700"
              color="coral.500" _hover={{ color: 'coral.700' }}
              display="flex" alignItems="center" gap="3px">
              Ver todas <Icon as={FiChevronRight} boxSize="11px" />
            </Text>
          </Flex>
        </Box>
      )}
    </SectionCard>
  );
};

// ── main component ─────────────────────────────────────────────────────────────

const AntecedentesTab = ({ customer, onUpdate, hasAgenda, userSurveys }) => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false); // indicador visible mientras corre el auto-sync
  const autoSyncedFor = useRef(null); // customer.id ya auto-sincronizado en este montaje

  const fetchConfigs = () => {
    setLoading(true);
    setLoadError(null);
    loadFormSyncConfigs(true) // forceRefresh — nunca usar cache aquí
      .then(data => {
        setConfigs(data || {});
      })
      .catch(err => {
        const status = err?.response?.status;
        const msg    = err?.response?.data?.message || err?.message || 'Error desconocido';
        setLoadError(`${status ? `HTTP ${status} — ` : ''}${msg}`);
        setConfigs({});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchConfigs(); }, []);

  // Todos los configs se muestran — trigger solo afecta cuándo se sincroniza, no si se muestra
  const allConfigs    = Object.values(configs);
  const activeConfigs = allConfigs; // sin filtro por trigger
  const col1 = activeConfigs.filter(c => !c.column || c.column === 1);
  const col2 = activeConfigs.filter(c => c.column === 2);

  // Auto-sync al entrar al tab — sólo rellena categorías sin datos aún (no pisa
  // ediciones existentes). autoSyncing se muestra MIENTRAS corre (indicador, no
  // toast) para que no se sienta como que la info "aparece de golpe" sin aviso.
  useEffect(() => {
    if (loading || loadError) return;
    if (!customer?.id || activeConfigs.length === 0) return;
    if (!userSurveys || userSurveys.length === 0) return;
    if (autoSyncedFor.current === customer.id) return;
    autoSyncedFor.current = customer.id;

    setAutoSyncing(true);
    runBackfillSync({ customer, userSurveys, configs, mode: 'auto' })
      .then(({ synced }) => { if (synced > 0) onUpdate(); })
      .catch(() => { /* silencioso — el botón manual sigue disponible */ })
      .finally(() => setAutoSyncing(false));
  }, [loading, loadError, customer, userSurveys, configs, activeConfigs.length, onUpdate]);

  // Alerta médica — busca en meta_data si algún config de salud tiene diagnostico/alergias
  const diagnostico = getMeta(customer, 'diagnostico');
  const alergias    = getMeta(customer, 'alergias');

  if (loading) {
    return (
      <Flex align="center" justify="center" p="48px">
        <Spinner color="coral.500" size="lg" />
      </Flex>
    );
  }

  if (loadError) {
    return (
      <Box p="24px" fontFamily="jakarta">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
          <VStack spacing="16px" align="stretch">
            <DatosPersonalesCard customer={customer} />
            <Alert status="error" borderRadius="14px">
              <AlertIcon />
              <Box flex={1}>
                <Text fontWeight="700" fontSize="13.5px">Error al cargar configuración</Text>
                <Text fontSize="12.5px" fontFamily="mono" mt="4px">{loadError}</Text>
              </Box>
              <Button size="xs" ml="12px" onClick={fetchConfigs} leftIcon={<Icon as={FiRefreshCw} />}>
                Reintentar
              </Button>
            </Alert>
          </VStack>
          <VStack spacing="16px" align="stretch">
              <ProximaCitaCard studentRut={customer.username} />
            </VStack>
        </SimpleGrid>
      </Box>
    );
  }

  if (activeConfigs.length === 0) {
    return (
      <Box p="24px" fontFamily="jakarta">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
          <VStack spacing="16px" align="stretch">
            <DatosPersonalesCard customer={customer} />
            <Alert status={allConfigs.length > 0 ? 'warning' : 'info'} borderRadius="14px">
              <AlertIcon />
              <Box>
                {allConfigs.length > 0 ? (
                  <>
                    <Text fontWeight="700" fontSize="13.5px">
                      {allConfigs.length} config(s) encontradas, todas con trigger "manual"
                    </Text>
                    <Text fontSize="13px">
                      Cambia el trigger de las configs a <strong>auto</strong> en Sistema → Sync Formularios para que se muestren aquí.
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontWeight="700" fontSize="13.5px">Sin configuración de Sync Formularios</Text>
                    <Text fontSize="13px">
                      Ve a <strong>Sistema → Sync Formularios</strong> y configura los mapeos para poblar
                      automáticamente los antecedentes del alumno desde las fichas.
                    </Text>
                  </>
                )}
              </Box>
            </Alert>
          </VStack>
          <VStack spacing="16px" align="stretch">
              <ProximaCitaCard studentRut={customer.username} />
            </VStack>
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <Box p="24px" fontFamily="jakarta">

      {/* Alerta médica */}
      {(diagnostico || alergias) && (
        <Flex align="flex-start" gap="12px" p="14px 18px" mb="18px"
          bg="butter.bg" border="1px solid" borderColor="butter.ink" borderRadius="14px" color="butter.ink">
          <Icon as={FiAlertTriangle} boxSize="18px" mt="1px" flex="0 0 auto" />
          <Box>
            <Text fontWeight="700" fontSize="13.5px" mb="2px">
              Alerta médica{diagnostico ? ` — ${diagnostico}` : ''}
            </Text>
            {alergias && <Text fontSize="13px" fontWeight="500" opacity={0.85}>{alergias}</Text>}
          </Box>
        </Flex>
      )}

      {/* Indicador de auto-sync en curso — el botón manual de backfill
          está oculto por ahora (ver SHOW_BACKFILL_BUTTON), esto sigue
          mostrándose solo mientras corre el auto-sync al entrar al tab. */}
      {autoSyncing && (
        <Flex align="center" mb="16px" minH="32px">
          <HStack spacing="8px">
            <Spinner size="xs" color="coral.400" />
            <Text fontSize="12.5px" color="ink.400" fontWeight="600">
              Actualizando desde fichas...
            </Text>
          </HStack>
        </Flex>
      )}
      {SHOW_BACKFILL_BUTTON && userSurveys?.length > 0 && (
        <Flex justify="flex-end" mb="16px">
          <BackfillButton
            customer={customer}
            userSurveys={userSurveys}
            configs={configs}
            onUpdate={onUpdate}
            disabled={autoSyncing}
          />
        </Flex>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">

        {/* Columna 1 */}
        <VStack spacing="16px" align="stretch">
          <DatosPersonalesCard customer={customer} />
          {col1.map((cfg, i) => (
            <DynamicCard key={cfg.slug} config={cfg} customer={customer}
              onUpdate={onUpdate} colorIdx={i + 1} userSurveys={userSurveys} />
          ))}
        </VStack>

        {/* Columna 2 */}
        <VStack spacing="16px" align="stretch">
          {col2.map((cfg, i) => (
            <DynamicCard key={cfg.slug} config={cfg} customer={customer}
              onUpdate={onUpdate} colorIdx={i + 2} userSurveys={userSurveys} />
          ))}

          {/* Citas */}
          <ProximaCitaCard studentRut={customer.username} />
        </VStack>

      </SimpleGrid>
    </Box>
  );
};

export default AntecedentesTab;
